/**
 * Story 13.15: External WebGL Preview Launcher
 *
 * Opens Unity WebGL preview in a separate browser tab using React component.
 * This eliminates:
 * - Background loading on non-Preview tabs
 * - Keyboard hijacking from SDL2 event listeners
 * - GLctx errors from cleanup race conditions
 * - Complex lazy mount/unmount patterns
 *
 * The new tab loads a React route (/preview) that uses ProjectPreview component
 * with react-unity-webgl, ensuring consistent behavior with the old in-app preview.
 */

export interface PreviewParams {
  projectId?: number;
  projectGroupId?: number;
  jwt: string;
  name?: string;
}

export interface PreviewResult {
  success: boolean;
  error?: string;
}

/**
 * Opens ProjectPreviewWebGL in a new browser tab with authentication and configuration.
 *
 * @param params - Preview parameters including projectId/projectGroupId and JWT
 * @returns PreviewResult indicating success/failure and specific error types
 *
 * @example
 * ```ts
 * const result = await openPreviewInNewTab({
 *   projectGroupId: 123,
 *   jwt: currentJwt
 * });
 *
 * if (!result.success) {
 *   // Show generic error
 * }
 * ```
 */
export function openPreviewInNewTab(params: PreviewParams): Promise<PreviewResult> {
  const { projectId, projectGroupId, jwt, name } = params;

  if (!jwt || jwt.trim() === "") {
    return Promise.resolve({ success: false, error: "JWT is required and must be non-empty" });
  }

  if (!projectId && !projectGroupId) {
    return Promise.resolve({
      success: false,
      error: "Either projectId or projectGroupId must be provided",
    });
  }

  try {
    const url = new URL(window.location.origin + "/preview");

    if (projectGroupId) {
      url.searchParams.set("projectGroupId", String(projectGroupId));
    }
    if (projectId) {
      url.searchParams.set("projectId", String(projectId));
    }

    if (name) {
      url.searchParams.set("name", name);
    }

    const windowTarget = name
      ? `wixar-preview-${name.replace(/[^a-zA-Z0-9]/g, "_")}`
      : "wixar-preview";
    window.open(url.toString(), windowTarget, "noopener,noreferrer");

    return Promise.resolve({ success: true });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return Promise.resolve({ success: false, error: `Failed to open preview: ${errorMessage}` });
  }
}
